import type { PrefetchVariant } from '~/core/entities/property';

import {
	isVideoCodecTypeSupported,
	predictStartLevel,
	relaxHevcCodec
} from '~/composables/useHlsPlayer/hevcDecodeGating';
import { getPrimedManifest } from '~/composables/useHlsPlayer/shakaLoader';
import { startTarget } from '~/composables/useHlsPlayer/startTarget';
import { cacheResponse } from '~/composables/useHlsPlayer/videoSegmentCache';

/**
 * Returns the video URL with a cache-buster (`_cb=1`) to avoid stale CORS preflight cache.
 * Ensures URL is absolute — critical for iOS Safari HLS manifest resolution.
 */
export function getProxiedVideoUrl(
	videoUrl: string | null | undefined
): string | null {
	if (videoUrl == null || videoUrl === '') return null;

	// Convert relative URL to absolute (critical for SSR on iOS)
	const absoluteUrl = (() => {
		if (typeof window === 'undefined' || videoUrl.startsWith('http')) {
			return videoUrl;
		}
		try {
			return new URL(videoUrl, window.location.href).href;
		} catch {
			// If URL parsing fails, use as-is
			return videoUrl;
		}
	})();

	const separator = absoluteUrl.includes('?') ? '&' : '?';
	return `${absoluteUrl}${separator}_cb=1`;
}

export interface ThumbnailOptions {
	width?: number;
	height?: number;
	quality?: number;
	format?: string;
}

/**
 * Composable that returns a function to generate optimized thumbnail URLs
 * via NuxtImage's configured provider (e.g. Netlify Image CDN in production).
 * Must be called inside `<script setup>` or another composable.
 */
export function useOptimizedThumbnail() {
	const img = useImage();

	return (
		thumbnailUrl: string | null,
		options: ThumbnailOptions = {}
	): string | null => {
		if (!thumbnailUrl) return null;

		const { width, height, quality = 80, format = 'jpg' } = options;

		return img(thumbnailUrl, {
			format,
			quality,
			...(width && { width }),
			...(height && { height }),
			...((width || height) && { fit: 'cover' })
		});
	};
}

export type VideoOrientation = 'portrait' | 'landscape' | 'square';
export const COMPACT_HERO_VIDEO_MAX_WIDTH = 700;

/**
 * Determines video orientation based on width and height.
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns 'portrait' if height > width, 'landscape' if width > height, 'square' if equal
 */
export function getVideoOrientation(
	width: number | undefined,
	height: number | undefined
): VideoOrientation {
	if (!width || !height || width === 0 || height === 0) {
		// Default to portrait for mobile-first video reels
		return 'portrait';
	}

	if (height > width) return 'portrait';
	if (width > height) return 'landscape';
	return 'square';
}

/**
 * Checks if the video is in portrait orientation (height > width).
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns true if video is portrait, false otherwise
 */
export function isPortraitVideo(
	width: number | undefined,
	height: number | undefined
): boolean {
	return getVideoOrientation(width, height) === 'portrait';
}

/**
 * Checks if the video is in landscape orientation (width > height).
 * @param width - Video width in pixels
 * @param height - Video height in pixels
 * @returns true if video is landscape, false otherwise
 */
export function isLandscapeVideo(
	width: number | undefined,
	height: number | undefined
): boolean {
	return getVideoOrientation(width, height) === 'landscape';
}

/**
 * Hero videos with smaller widths should use the compact first-slot layout,
 * even if the encoded orientation metadata is not strictly portrait.
 */
export function shouldUseCompactHeroVideoLayout(
	width: number | undefined,
	height: number | undefined
): boolean {
	if (width && width > 0 && width <= COMPACT_HERO_VIDEO_MAX_WIDTH) {
		return true;
	}

	return isPortraitVideo(width, height);
}

// ---------------------------------------------------------------------------
// HLS manifest parsing & segment prefetch helpers
// ---------------------------------------------------------------------------

interface HlsVariant {
	bandwidth: number;
	url: string;
	videoCodec?: string;
	width?: number;
	height?: number;
}

/**
 * Resolves a potentially relative URL against a base URL.
 * Follows HLS spec: relative paths inherit the base origin + directory,
 * but NOT query strings or fragments from the parent.
 */
function resolveUrl(base: string, relative: string): string {
	if (relative.startsWith('http://') || relative.startsWith('https://')) {
		return relative;
	}
	const baseUrl = new URL(base);
	if (relative.startsWith('/')) {
		return `${baseUrl.origin}${relative}`;
	}
	const basePath = baseUrl.pathname.substring(
		0,
		baseUrl.pathname.lastIndexOf('/') + 1
	);
	return `${baseUrl.origin}${basePath}${relative}`;
}

/**
 * Parses an HLS master playlist and returns variant streams sorted by bandwidth (ascending).
 * Returns empty array if the playlist is a media playlist (no variants).
 */
function parseMasterPlaylist(text: string): HlsVariant[] {
	const lines = text.split('\n').map(l => l.trim());
	const variants: HlsVariant[] = [];

	for (let i = 0; i < lines.length - 1; i++) {
		const line = lines[i] ?? '';
		if (!line.startsWith('#EXT-X-STREAM-INF:')) continue;

		// (?<!-) keeps AVERAGE-BANDWIDTH from matching first: CMAF masters are
		// tool-generated and may declare both attributes on the same line.
		const bwMatch = line.match(/(?<!-)BANDWIDTH=(\d+)/);
		if (!bwMatch) continue;

		// The URL is the next non-empty, non-comment line
		const url = lines[i + 1] ?? '';
		if (!url || url.startsWith('#')) continue;

		const size = line.match(/RESOLUTION=(\d+)x(\d+)/);
		const width = size ? Number(size[1]) : undefined;
		const height = size ? Number(size[2]) : undefined;
		const videoCodec = line
			.match(/CODECS="([^"]*)"/)?.[1]
			?.split(',')
			.map(codec => codec.trim())
			.find(codec => /^(avc|hvc|hev|av01|vp09)/i.test(codec));
		variants.push({
			bandwidth: Number(bwMatch[1]),
			url,
			...(videoCodec ? { videoCodec } : {}),
			...(width && height ? { width, height } : {})
		});
	}

	return variants.sort((a, b) => a.bandwidth - b.bandwidth);
}

/**
 * The variant the player will start on: the codecs hls.js keeps (with the
 * overstated HEVC level relaxed the same way), then predictStartLevel, the
 * player's own pick. Undefined when the master carries no variants.
 */
async function startVariant(
	variants: HlsVariant[],
	cssShortSide?: number
): Promise<HlsVariant | undefined> {
	const playable = variants
		.map(variant =>
			variant.videoCodec
				? {
						...variant,
						videoCodec: relaxHevcCodec(
							variant.videoCodec,
							variant.width,
							variant.height,
							isVideoCodecTypeSupported
						)
					}
				: variant
		)
		.filter(
			variant =>
				!variant.videoCodec || isVideoCodecTypeSupported(variant.videoCodec)
		);
	const index = await predictStartLevel(
		playable.map(variant => ({ ...variant, bitrate: variant.bandwidth })),
		startTarget(cssShortSide)
	);
	return playable[index];
}

/**
 * The ladder rung the player will start on. Only the master carries the
 * codecs that decide it (the lower rungs of the Lambda ladder all declare
 * ~1.06 Mbps, so the cheapest by bandwidth is a coin toss): one round trip
 * before the rest of the warm, none when the SSR shipped it. Undefined when the
 * master cannot tell.
 */
async function ladderStartRung(
	masterUrl: string,
	ladder: PrefetchVariant[],
	fetchOpts: RequestInit,
	cssShortSide?: number
): Promise<PrefetchVariant | undefined> {
	try {
		const start = await startVariant(
			parseMasterPlaylist(await readMasterText(masterUrl, fetchOpts)),
			cssShortSide
		);
		if (!start) return undefined;
		const path = new URL(resolveUrl(masterUrl, start.url)).pathname;
		return ladder.find(rung => new URL(rung.playlistUrl).pathname === path);
	} catch {
		// Best-effort warm: without the master, fall back to the bandwidth rule.
		return undefined;
	}
}

/**
 * Parses an HLS media playlist and returns the first N segment URLs.
 */
function parseMediaPlaylist(text: string, maxSegments: number = 1): string[] {
	const lines = text.split('\n').map(l => l.trim());
	const segments: string[] = [];

	for (const line of lines) {
		if (line.startsWith('#') || line === '') continue;
		segments.push(line);
		if (segments.length >= maxSegments) break;
	}

	return segments;
}

/**
 * Extracts the `v=HASH` version parameter from a URL.
 * Shaka's custom scheme handler (`getVersionedUrl`) appends this to all
 * `.m3u8` and `.ts` requests, so our prefetched URLs must include it too
 * for the browser/CDN cache to hit.
 */
function extractVersionParam(url: string): string {
	const match = url.match(/[?&]v=([^&]+)/);
	return match?.[1] ?? '';
}

/** Drops any existing `?v=`/`&v=` so a hash can be re-applied consistently. */
function stripVersion(url: string): string {
	return url.replace(/([?&])v=[^&]*(&|$)/, (_, sep: string, tail: string) =>
		tail === '&' ? sep : ''
	);
}

/** Appends `?v=HASH` to a URL if a version hash is available. */
function appendVersion(url: string, versionHash: string): string {
	if (!versionHash) return url;
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}v=${versionHash}`;
}

/**
 * Warms the default audio rendition (playlist + init + first segment) of a
 * separate-audio stream. Best-effort and fire-and-forget: callers never wait
 * on it, and muxed/silent streams are a cheap no-op.
 */
async function warmDefaultAudioChain(
	masterUrl: string,
	versionHash: string,
	fetchOpts: RequestInit
): Promise<void> {
	try {
		const text =
			getPrimedManifest(masterUrl) ??
			(await (await fetch(masterUrl, fetchOpts)).text());
		await warmDefaultAudioChainFromText(
			text,
			masterUrl,
			versionHash,
			fetchOpts
		);
	} catch {
		// Warm-up only — never propagate.
	}
}

async function warmDefaultAudioChainFromText(
	masterText: string,
	masterUrl: string,
	versionHash: string,
	fetchOpts: RequestInit
): Promise<void> {
	try {
		const audioUri = parseDefaultAudioUri(masterText);
		if (!audioUri) return;

		const audioPlaylistUrl = appendVersion(
			resolveUrl(masterUrl, audioUri),
			versionHash
		);
		const audioResponse = await fetchAndCache(audioPlaylistUrl, fetchOpts);
		const audioText = await audioResponse.text();
		const audioInit = audioText.match(/#EXT-X-MAP:URI="([^"]+)"/)?.[1];
		const audioFirst = parseMediaPlaylist(audioText, 1)[0];

		await Promise.allSettled([
			...(audioInit
				? [
						fetchAndCache(
							appendVersion(
								resolveUrl(audioPlaylistUrl, audioInit),
								versionHash
							),
							fetchOpts
						)
					]
				: []),
			...(audioFirst
				? [
						fetchAndCache(
							appendVersion(
								resolveUrl(audioPlaylistUrl, audioFirst),
								versionHash
							),
							fetchOpts
						)
					]
				: [])
		]);
	} catch {
		// Warm-up only — never propagate.
	}
}

/**
 * Fetches a URL and stores the response in the shared CacheStorage so that
 * Shaka Player's scheme handler finds it and skips the network request.
 * Clones the response upfront so the caller can safely consume the original.
 */
async function fetchAndCache(
	url: string,
	fetchOpts: RequestInit
): Promise<Response> {
	const response = await fetch(url, fetchOpts);
	// A non-ok body is not a playlist or segment: never cache or parse it.
	// 403/429 is the WAF rate limit doing its job, so it is not logged.
	if (!response.ok) {
		if (response.status !== 403 && response.status !== 429) {
			console.error('[prefetch] HLS warm fetch failed', {
				status: response.status,
				url
			});
		}
		throw new Error(`HTTP ${response.status} for ${url}`);
	}
	// Clone immediately — cacheResponse is async and may run after the caller
	// has already consumed the original body.
	cacheResponse(url, response.clone()).catch(() => {});
	return response;
}

/** The master's text: the SSR payload's copy when the page shipped one. */
async function readMasterText(
	masterUrl: string,
	fetchOpts: RequestInit
): Promise<string> {
	return (
		getPrimedManifest(masterUrl) ??
		(await fetchAndCache(masterUrl, fetchOpts)).text()
	);
}

/**
 * Prefetches the first segment of an HLS video variant.
 * Responses are stored in CacheStorage so Shaka reuses them instead of
 * re-downloading. Returns the resolved variant playlist URL (used by iOS
 * quality cache to bypass AVPlayer's conservative quality selection).
 *
 * @param quality - 'lowest' warms the rung the player starts on, the same
 *                  pickStartLevel the player pins (default);
 *                  'highest' for 1080p preload when network is proven good.
 * @param maxSegmentBps - when set, the segment download is skipped if the
 *                  chosen variant declares a higher BANDWIDTH. Guards slow
 *                  networks against old single-variant videos whose "lowest"
 *                  rung is a 4-9 Mbps original (a 4+ MB first segment that
 *                  would hog the pipe for 30s+ on 3G). Manifests still warm.
 * @param ladder - the ladder already resolved by the publisher. Skips the
 *                  discovery chain, so warming costs two round trips instead
 *                  of three ('lowest' still reads the master for the codecs
 *                  that decide the player's start rung), and lets the rung be
 *                  picked by declared bandwidth. Absent for videos published
 *                  before the field existed, which fall back to discovery below.
 */
export async function prefetchFirstSegment(
	masterUrl: string,
	signal: AbortSignal,
	quality: 'lowest' | 'highest' = 'lowest',
	maxSegmentBps?: number,
	ladder?: PrefetchVariant[] | null,
	/**
	 * So os manifestos (poucos KB). Para video legado sem escada, onde o
	 * segmento pode ter megabytes e a especulacao nao se paga.
	 */
	manifestOnly = false,
	/** CSS short side of the element that will play it; the viewport if unset. */
	cssShortSide?: number
): Promise<string | null> {
	const fetchOpts = { priority: 'low', signal } as RequestInit;
	const versionHash = extractVersionParam(masterUrl);

	// 0. Pre-resolved shortcut: warm playlist, init and segment in parallel
	// instead of discovering them in a serial chain (master -> parse ->
	// playlist -> parse -> segment). The master is warmed too — Shaka asks for it first on load,
	// so dropping it would only move the round trip from the prefetch to the
	// player, which is the wrong side to pay it on.
	//
	// Carrying the whole ladder (not just the lowest rung) is what lets the
	// bandwidth cap and the 'highest' request be honoured here: both need the
	// rung's declared BANDWIDTH, which used to mean reading the master.
	if (ladder?.length) {
		const ascending = [...ladder].sort((a, b) => a.bandwidth - b.bandwidth);
		const affordable =
			maxSegmentBps === undefined
				? ascending
				: ascending.filter(v => v.bandwidth <= maxSegmentBps);

		// 'lowest' warms the rung the player starts on, not the cheapest one.
		const start =
			quality === 'lowest'
				? await ladderStartRung(masterUrl, ladder, fetchOpts, cssShortSide)
				: undefined;
		const masterWarmed =
			quality === 'lowest' || getPrimedManifest(masterUrl) !== undefined;
		const fits = (rung: PrefetchVariant) =>
			maxSegmentBps === undefined || rung.bandwidth <= maxSegmentBps;
		let chosen: PrefetchVariant | undefined;
		if (start) chosen = fits(start) ? start : undefined;
		else
			chosen =
				quality === 'highest'
					? affordable[affordable.length - 1]
					: affordable[0];

		if (chosen) {
			// Cache keys must be byte-identical to what Shaka will ask for, or
			// the warm-up is thrown away. Shaka resolves these relative to the
			// master (which drops the query) and its scheme handler appends
			// only the version — never _cb. So: normalise the API's own version
			// param to the master's hash, and do NOT proxy. Same convention as
			// the discovery path below, which caches raw and proxies only what
			// it returns.
			const playlistUrl = appendVersion(
				stripVersion(chosen.playlistUrl),
				versionHash
			);
			const segmentUrl = appendVersion(
				stripVersion(chosen.firstSegmentUrl),
				versionHash
			);
			// CMAF rungs also need the init segment before the first chunk.
			const initUrl = chosen.initUrl
				? appendVersion(stripVersion(chosen.initUrl), versionHash)
				: null;
			// allSettled: a rejection here is a warm that did not happen, never
			// an error the caller should see. Promise.all would also leave the
			// loser's rejection unhandled.
			await Promise.allSettled([
				...(masterWarmed ? [] : [fetchAndCache(masterUrl, fetchOpts)]),
				fetchAndCache(playlistUrl, fetchOpts),
				...(initUrl ? [fetchAndCache(initUrl, fetchOpts)] : []),
				fetchAndCache(segmentUrl, fetchOpts)
			]);
			// Separate-audio streams: warm the default audio chain too, or the
			// play() moment discovers it serially and stalls on it. Fire and
			// forget — the video warm above is what the caller waits for.
			void warmDefaultAudioChain(masterUrl, versionHash, fetchOpts);
			// The return value feeds the iOS quality cache, which does want the
			// proxied form.
			return getProxiedVideoUrl(playlistUrl) ?? playlistUrl;
		} else if (maxSegmentBps !== undefined) {
			// Every rung is heavier than this network can afford: warming the
			// manifests alone is what the discovery path does in the same spot.
			if (!masterWarmed) {
				await Promise.allSettled([fetchAndCache(masterUrl, fetchOpts)]);
			}
			return null;
		}
	}

	// 1. Fetch & parse master playlist — also cache for Shaka reuse
	const masterText = await readMasterText(masterUrl, fetchOpts);
	const variants = parseMasterPlaylist(masterText);
	void warmDefaultAudioChainFromText(
		masterText,
		masterUrl,
		versionHash,
		fetchOpts
	);

	if (variants.length === 0) {
		// Single-variant stream — master IS the media playlist. Bandwidth is
		// unknown here, so a maxSegmentBps caller opts out of the segment.
		if (manifestOnly || maxSegmentBps !== undefined) return null;
		const segments = parseMediaPlaylist(masterText, 1);
		const firstSegment = segments[0];
		if (firstSegment) {
			await fetchAndCache(
				appendVersion(resolveUrl(masterUrl, firstSegment), versionHash),
				fetchOpts
			);
		}
		return null;
	}

	// 2. Pick variant: the player's start rung, or the top one by bandwidth
	const chosen =
		quality === 'highest'
			? variants[variants.length - 1]
			: ((await startVariant(variants, cssShortSide)) ?? variants[0]);
	if (!chosen) return null;

	const variantUrl = appendVersion(
		resolveUrl(masterUrl, chosen.url),
		versionHash
	);

	// 3. Fetch & parse variant playlist — also cache for Shaka reuse
	const variantResponse = await fetchAndCache(variantUrl, fetchOpts);
	const variantText = await variantResponse.text();
	if (manifestOnly) return null;
	const segments = parseMediaPlaylist(variantText, 1);

	const firstSegment = segments[0];
	if (!firstSegment) return variantUrl;

	// 4. Prefetch the first segment (unless the variant is too heavy for the
	// current network — manifests are warmed above either way)
	if (maxSegmentBps !== undefined && chosen.bandwidth > maxSegmentBps) {
		return getProxiedVideoUrl(variantUrl) ?? variantUrl;
	}
	await fetchAndCache(
		appendVersion(resolveUrl(variantUrl, firstSegment), versionHash),
		fetchOpts
	);

	// Return the proxied variant URL for iOS quality cache
	return getProxiedVideoUrl(variantUrl) ?? variantUrl;
}

/**
 * Resolves the URL of the first segment of the lowest-quality variant.
 * Does NOT download the segment — used as a probe target for bandwidth measurement.
 */
export async function resolveFirstSegmentUrl(
	masterUrl: string,
	signal: AbortSignal
): Promise<string | null> {
	try {
		const fetchOpts = { priority: 'high', signal } as RequestInit;
		const versionHash = extractVersionParam(masterUrl);

		const masterResponse = await fetchAndCache(masterUrl, fetchOpts);
		const masterText = await masterResponse.text();
		const variants = parseMasterPlaylist(masterText);

		let firstSegmentRaw: string | undefined;
		let baseUrl: string;

		if (variants.length === 0) {
			firstSegmentRaw = parseMediaPlaylist(masterText, 1)[0];
			baseUrl = masterUrl;
		} else {
			const lowest = variants[0];
			if (!lowest) return null;
			const variantUrl = appendVersion(
				resolveUrl(masterUrl, lowest.url),
				versionHash
			);
			const variantResponse = await fetchAndCache(variantUrl, fetchOpts);
			const variantText = await variantResponse.text();
			firstSegmentRaw = parseMediaPlaylist(variantText, 1)[0];
			baseUrl = variantUrl;
		}

		if (!firstSegmentRaw) return null;
		return getProxiedVideoUrl(
			appendVersion(resolveUrl(baseUrl, firstSegmentRaw), versionHash)
		);
	} catch {
		return null;
	}
}

export interface HlsWarmupUrls {
	// Null when the stored video URL already IS the media playlist (old
	// single-variant videos) — there is no separate variant playlist to preload.
	variantPlaylistUrl: string | null;
	firstSegmentUrl: string | null;
	// CMAF only (EXT-X-MAP): the player needs it before the first segment.
	initSegmentUrl?: string | null;
	// Default audio rendition (EXT-X-MEDIA), when audio ships separately:
	// playback stalls on this chain if only the video side is warmed.
	audioPlaylistUrl?: string | null;
	audioInitSegmentUrl?: string | null;
	audioFirstSegmentUrl?: string | null;
	/** Texto dos manifests já buscados no SSR, indexado pela URL que o player
	 *  vai requisitar — o loader consome sem nenhum round-trip de rede. */
	manifests?: Record<string, string>;
}

/**
 * URI of the default (or first) audio rendition in a master playlist, or null
 * for muxed/silent streams.
 */
function parseDefaultAudioUri(masterText: string): string | null {
	const audioLines = masterText
		.split('\n')
		.filter(l => l.startsWith('#EXT-X-MEDIA:') && l.includes('TYPE=AUDIO'));
	const preferred =
		audioLines.find(l => l.includes('DEFAULT=YES')) ?? audioLines[0];
	return preferred?.match(/URI="([^"]+)"/)?.[1] ?? null;
}

// Two-tier cache: per-process memory (L1, 0ms) backed by the shared Redis (L2)
// so fresh instances start warm across the fleet. Failures are NOT cached: a
// transient CDN error must not pin a video to "no hints".
//
// This used to assume the manifest behind a URL never changes, since the URL
// carries the assembly id and a version hash. That assumption does not hold:
// the CODECS repair rewrites master.m3u8 in place and keeps the same hash, so
// the same URL can answer with different bytes. Both tiers therefore expire.
//
// Without an expiry the L1 map was the worst of the three layers holding this
// manifest: CloudFront ages out in two hours, Redis is wiped on every deploy,
// and this one survived until the process died. A corrected manifest reached
// the shorts feed only when the service happened to restart.
//
// The staleness bound is the REDIS ttl, not the memory one. An expired L1 entry
// is refilled from Redis and re-stamped, so the short memory ttl only decides
// how often a process re-reads L2, never how old the answer can be.
const WARMUP_MEMORY_TTL_MS = 5 * 60 * 1000;

interface WarmupEntry {
	value: HlsWarmupUrls;
	storedAt: number;
}

const warmupCache = new Map<string, WarmupEntry>();
const WARMUP_CACHE_MAX_ENTRIES = 500;
const WARMUP_REDIS_PREFIX = 'hls:warmup:v2:';
// Bounded on purpose. A miss costs two small conditional fetches during SSR and
// the entry is written straight back; a week of staleness costs every viewer of
// a repaired video the wrong codec ladder until the next deploy.
const WARMUP_REDIS_TTL_SECONDS = 60 * 60; // 1 hour
// A warmup cache read is an optimization — never worth more TTFB than this.
const WARMUP_REDIS_READ_TIMEOUT_MS = 150;

interface RedisLike {
	status: string;
	get(key: string): Promise<string | null>;
	set(key: string, value: string, mode: 'EX', ttl: number): Promise<unknown>;
}

// Same global-client pattern as server/runtime/feature-flags.ts — the shared
// ioredis instance is exposed on globalThis by the server plugin. Null on the
// client and whenever Redis is unavailable (never block SSR on Redis).
function getServerRedis(): RedisLike | null {
	if (!import.meta.server) return null;
	const redis = (globalThis as { __redis?: RedisLike }).__redis;
	return redis && redis.status === 'ready' ? redis : null;
}

/**
 * Single door into the L1 map, so the entry cap holds on every path that fills
 * it. Filling it straight from a Redis hit used to skip the cap, and on a warm
 * fleet that is the path almost every read takes.
 */
function rememberWarmup(masterUrl: string, value: HlsWarmupUrls): void {
	if (warmupCache.size >= WARMUP_CACHE_MAX_ENTRIES) {
		const oldest = warmupCache.keys().next().value;
		if (oldest !== undefined) warmupCache.delete(oldest);
	}
	warmupCache.set(masterUrl, { value, storedAt: Date.now() });
}

function storeWarmupResult(masterUrl: string, resolved: HlsWarmupUrls): void {
	rememberWarmup(masterUrl, resolved);

	getServerRedis()
		?.set(
			`${WARMUP_REDIS_PREFIX}${masterUrl}`,
			JSON.stringify(resolved),
			'EX',
			WARMUP_REDIS_TTL_SECONDS
		)
		.catch(() => {});
}

async function readWarmupCache(
	masterUrl: string
): Promise<HlsWarmupUrls | null> {
	const memory = warmupCache.get(masterUrl);
	if (memory) {
		if (Date.now() - memory.storedAt < WARMUP_MEMORY_TTL_MS)
			return memory.value;
		warmupCache.delete(masterUrl);
	}

	const redis = getServerRedis();
	if (!redis) return null;
	try {
		// This read is awaited during SSR, and the caller's 800ms warmup signal
		// does not cover it — a "ready" Redis with high latency must degrade to
		// a cache miss instead of holding the page's TTFB.
		const raw = await Promise.race([
			redis.get(`${WARMUP_REDIS_PREFIX}${masterUrl}`),
			new Promise<null>(resolve =>
				setTimeout(resolve, WARMUP_REDIS_READ_TIMEOUT_MS, null)
			)
		]);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as HlsWarmupUrls;
		// variantPlaylistUrl is null by design for single-variant videos, so
		// validate the object shape instead of that nullable field.
		if (!parsed || typeof parsed !== 'object' || !('firstSegmentUrl' in parsed))
			return null;
		rememberWarmup(masterUrl, parsed);
		return parsed;
	} catch {
		return null;
	}
}

/**
 * Server-side resolver for SSR head preloads: fetches the master manifest and
 * the lowest-bandwidth variant playlist, returning their URLs so the page can
 * emit <link rel="preload"> hints. URLs carry the v=HASH version (matching
 * Shaka's request URLs exactly) but NOT _cb — the preload cache matches by
 * exact URL and Shaka's scheme handler does not append _cb.
 *
 * Never throws — returns null on any failure so SSR renders without hints.
 */
export async function resolveHlsWarmupUrls(
	masterUrl: string,
	signal: AbortSignal
): Promise<HlsWarmupUrls | null> {
	const cached = await readWarmupCache(masterUrl);
	if (cached) return cached;

	try {
		const versionHash = extractVersionParam(masterUrl);

		const masterResponse = await fetch(masterUrl, { signal });
		if (!masterResponse.ok) return null;
		const masterText = await masterResponse.text();
		const variants = parseMasterPlaylist(masterText);
		const lowest = variants[0];

		// Old single-variant videos store the media playlist URL directly — no
		// variant chain to walk, but the first segment is right there.
		if (!lowest) {
			const directSegment = parseMediaPlaylist(masterText, 1)[0];
			if (!directSegment) return null;
			const resolved: HlsWarmupUrls = {
				variantPlaylistUrl: null,
				firstSegmentUrl: appendVersion(
					resolveUrl(masterUrl, directSegment),
					versionHash
				)
			};
			storeWarmupResult(masterUrl, resolved);
			return resolved;
		}

		const variantPlaylistUrl = appendVersion(
			resolveUrl(masterUrl, lowest.url),
			versionHash
		);

		const variantResponse = await fetch(variantPlaylistUrl, { signal });
		if (!variantResponse.ok)
			return { variantPlaylistUrl, firstSegmentUrl: null };
		const variantText = await variantResponse.text();
		const firstSegment = parseMediaPlaylist(variantText, 1)[0];
		const initSegment = variantText.match(/#EXT-X-MAP:URI="([^"]+)"/)?.[1];

		const resolved: HlsWarmupUrls = {
			manifests: {
				[masterUrl]: masterText,
				[variantPlaylistUrl]: variantText
			},
			variantPlaylistUrl,
			firstSegmentUrl: firstSegment
				? appendVersion(
						resolveUrl(variantPlaylistUrl, firstSegment),
						versionHash
					)
				: null,
			initSegmentUrl: initSegment
				? appendVersion(
						resolveUrl(variantPlaylistUrl, initSegment),
						versionHash
					)
				: null
		};

		const audioUri = parseDefaultAudioUri(masterText);
		if (audioUri) {
			const audioPlaylistUrl = appendVersion(
				resolveUrl(masterUrl, audioUri),
				versionHash
			);
			resolved.audioPlaylistUrl = audioPlaylistUrl;
			const audioResponse = await fetch(audioPlaylistUrl, { signal });
			if (audioResponse.ok) {
				const audioText = await audioResponse.text();
				if (resolved.manifests)
					resolved.manifests[audioPlaylistUrl] = audioText;
				const audioInit = audioText.match(/#EXT-X-MAP:URI="([^"]+)"/)?.[1];
				const audioFirst = parseMediaPlaylist(audioText, 1)[0];
				resolved.audioInitSegmentUrl = audioInit
					? appendVersion(resolveUrl(audioPlaylistUrl, audioInit), versionHash)
					: null;
				resolved.audioFirstSegmentUrl = audioFirst
					? appendVersion(resolveUrl(audioPlaylistUrl, audioFirst), versionHash)
					: null;
			}
		}

		storeWarmupResult(masterUrl, resolved);

		return resolved;
	} catch {
		return null;
	}
}

/**
 * Fetches the master manifest and returns variant playlist URLs sorted by
 * bandwidth ascending (lowest quality first). Each URL has _cb=1 appended so
 * CORS preflight caching is bypassed consistently with the rest of the app.
 *
 * Used by the iOS quality-upgrade logic in HlsVideoPlayer: the caller steps
 * through the array one entry at a time as the network proves itself capable.
 * Returns an empty array when the URL is already a single-variant (media)
 * playlist or when the fetch fails.
 */
export async function resolveVariantUrls(
	masterUrl: string,
	signal: AbortSignal
): Promise<string[]> {
	try {
		const response = await fetch(masterUrl, {
			signal,
			priority: 'low'
		} as RequestInit);
		if (!response.ok) return [];

		const text = await response.text();
		const variants = parseMasterPlaylist(text);
		if (variants.length === 0) return [];

		const versionHash = extractVersionParam(masterUrl);
		return variants.map(v => {
			const resolved = appendVersion(resolveUrl(masterUrl, v.url), versionHash);
			return getProxiedVideoUrl(resolved)!;
		});
	} catch {
		return [];
	}
}

/**
 * Composable for video URL management.
 * Provides reactive utilities for video URLs.
 */
export function useVideoUrls() {
	return {
		getProxiedVideoUrl,
		getOptimizedThumbnailUrl: useOptimizedThumbnail(),
		getVideoOrientation,
		isPortraitVideo,
		isLandscapeVideo,
		prefetchFirstSegment
	};
}
